/* eslint-disable max-lines */
import { useState, useEffect, useRef } from 'react';
import { type AxiosError } from 'axios';
import {
  AlertTriangle,
  Bot,
  CheckCircle,
  Download,
  Copy,
  XCircle,
  Loader2,
  Lock
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useCreateAiSummary,
  type AiSummaryJob
} from '@utils/api/documents/use-create-ai-summary.tsx';
import { useGetAiSummary } from '@utils/api/documents/use-get-ai-summary.tsx';
import { useCancelAiSummary } from '@utils/api/documents/use-cancel-ai-summary.tsx';
import cn from 'classnames';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { type Elements } from '@utils/providers/theme-context.tsx';
import { type AiSummaryQuota } from '@utils/types/entitlements.ts';

type Props = {
  documentId: string;
  disabled?: boolean;
  currentRevisionId?: number | null;
  /**
   * Timestamp of the last successful server save (from transcript context).
   * Used to detect edits since the summary was generated (#2179).
   */
  lastServerSync?: number | null;
  /**
   * Allowance from the organization bootstrap. Lets the confirmation step show
   * what a summary will cost before one has ever been run; the job response
   * only knows the allowance after the fact.
   */
  quota?: AiSummaryQuota | null;
  /**
   * When false, the component skips rendering its own trigger button and only
   * shows the modal. Combine with `open`/`onOpenChange` to have the parent
   * (e.g. an external ButtonGroup) drive open state.
   */
  renderTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type BlockReason =
  | { kind: 'quota_exhausted'; reset_at: string | null; limit: number | null }
  | { kind: 'feature_not_enabled' };

const TERMINAL_CLEAR = ['failed', 'cancelled'];

const jobKey = (docId: string) => `ai-summary-job-${docId}`;
const editedSinceKey = (docId: string) => `ai-summary-edited-since-${docId}`;

export const TranscriptSummarizeButton = ({
  documentId,
  disabled,
  currentRevisionId,
  lastServerSync,
  quota: entitlementQuota,
  renderTrigger = true,
  open,
  onOpenChange
}: Props) => {
  const { t } = useTranslation();
  const aiDisclosure =
    t('ai-summary-disclosure') ||
    'AI-generated summary · AI output may contain errors and should be reviewed before use or publication.';
  const { getColour } = useThemeContext();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setIsOpen = (value: boolean) => {
    if (isControlled) onOpenChange?.(value);
    else setInternalOpen(value);
  };
  const [activeJobId, setActiveJobId] = useState<string | null>(() =>
    localStorage.getItem(jobKey(documentId))
  );
  // True if the document has been saved at least once since the summary was generated.
  // Stored in localStorage so it survives page reloads.
  const [editedSinceSummary, setEditedSinceSummary] = useState<boolean>(
    () => localStorage.getItem(editedSinceKey(documentId)) === 'true'
  );
  const prevLastServerSync = useRef(lastServerSync);
  const [stylePrompt, setStylePrompt] = useState('');
  const [blockReason, setBlockReason] = useState<BlockReason | null>(null);

  const { mutate: createJob, isLoading: isCreating } = useCreateAiSummary();
  const { data: job } = useGetAiSummary(activeJobId);
  const { mutate: cancelJob } = useCancelAiSummary();

  // Clear localStorage for failed/cancelled jobs so next open starts fresh.
  // Completed jobs stay in localStorage so the revision-mismatch warning can
  // appear on future visits; they are only cleared when the user clicks "New Summary".
  useEffect(() => {
    if (job?.status && TERMINAL_CLEAR.includes(job.status)) {
      localStorage.removeItem(jobKey(documentId));
      localStorage.removeItem(editedSinceKey(documentId));
    }
  }, [job?.status, documentId]);

  // When lastServerSync changes (a save completed) and a job exists, mark the document as edited.
  useEffect(() => {
    if (
      lastServerSync != null &&
      lastServerSync !== prevLastServerSync.current &&
      activeJobId != null
    ) {
      localStorage.setItem(editedSinceKey(documentId), 'true');
      setEditedSinceSummary(true);
    }
    prevLastServerSync.current = lastServerSync;
  }, [lastServerSync, documentId, activeJobId]);

  const persistJobId = (id: string | null) => {
    if (id) {
      localStorage.setItem(jobKey(documentId), id);
    } else {
      localStorage.removeItem(jobKey(documentId));
      localStorage.removeItem(editedSinceKey(documentId));
      setEditedSinceSummary(false);
    }
    setActiveJobId(id);
  };

  const isProcessing = job && ['queued', 'processing'].includes(job.status);

  const handleOpen = () => {
    setBlockReason(null);
    setIsOpen(true);
  };

  const handleStart = () => {
    createJob(
      {
        document_id: documentId,
        style_prompt: stylePrompt.trim() || undefined,
        revision_id: currentRevisionId ?? undefined
      },
      {
        onSuccess: data => {
          persistJobId(data.job_id);
        },
        onError: err => {
          const data = (
            err as AxiosError<{
              detail?: { error?: string; reset_at?: string; limit?: number };
            }>
          )?.response?.data;
          const detail = data?.detail;
          if (detail?.error === 'quota_exhausted') {
            setBlockReason({
              kind: 'quota_exhausted',
              reset_at: detail.reset_at ?? null,
              limit: detail.limit ?? null
            });
          } else if (detail?.error === 'feature_not_enabled') {
            setBlockReason({ kind: 'feature_not_enabled' });
          } else {
            toast.error(t('ai-summary-error') || 'Failed to start AI summary.');
          }
        }
      }
    );
  };

  const handleCancel = () => {
    if (!activeJobId) return;
    cancelJob(activeJobId, {
      onSuccess: () => {
        persistJobId(null);
      }
    });
  };

  const handleClose = () => setIsOpen(false);

  const handleCopy = () => {
    if (!job?.result) return;
    const text = buildPlainText(job.result, aiDisclosure);
    navigator.clipboard.writeText(text).then(() => {
      toast.success(t('copied') || 'Copied to clipboard');
    });
  };

  const handleDownload = () => {
    if (!job?.result) return;
    const text = buildPlainText(job.result, aiDisclosure);
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-summary-${documentId}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const quota = job?.quota ?? entitlementQuota ?? undefined;
  const remaining = quota?.remaining;

  const revisionMismatch = job?.status === 'completed' && editedSinceSummary;

  return (
    <>
      {renderTrigger && (
        <Tooltip label={t('ai-summary') || 'AI Summary'}>
          <button
            type="button"
            onClick={handleOpen}
            disabled={disabled}
            aria-label={t('ai-summary') || 'AI Summary'}
            className={cn(
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-2xl text-xs font-medium border transition-colors hover:opacity-80 shrink-0'
            )}
            style={{
              borderColor: getColour('border'),
              color: getColour('normalText')
            }}
          >
            <Bot
              size={12}
              style={{ color: getColour('icon') }}
              aria-hidden="true"
            />
            {t('ai-summary') || 'AI Summary'}
          </button>
        </Tooltip>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="medium"
        title={t('ai-summary') || 'AI Summary'}
        caption={
          quota?.enabled
            ? `${remaining != null ? `${remaining} ${t('remaining-this-month') || 'remaining this month'}` : t('unlimited') || 'Unlimited'} · ${quota.reset_at ? `${t('resets') || 'Resets'} ${quota.reset_at}` : ''}`
            : undefined
        }
        footerActions={
          blockReason
            ? [
                <Button
                  key="close"
                  label={t('close') || 'Close'}
                  size="small"
                  variant="smoke"
                  onClick={handleClose}
                />
              ]
            : job?.status === 'completed'
              ? [
                  <Button
                    key="copy"
                    label={t('copy') || 'Copy'}
                    size="small"
                    variant="secondary"
                    icon={<Copy className="h-4 w-4" />}
                    onClick={handleCopy}
                  />,
                  <Button
                    key="download"
                    label={t('download') || 'Download .md'}
                    size="small"
                    variant="secondary"
                    icon={<Download className="h-4 w-4" />}
                    onClick={handleDownload}
                  />,
                  <Button
                    key="new"
                    label={t('generate-new-summary') || 'New Summary'}
                    size="small"
                    variant="primary"
                    onClick={() => {
                      persistJobId(null);
                      handleStart();
                    }}
                    loading={isCreating}
                  />
                ]
              : isProcessing
                ? [
                    <Button
                      key="cancel"
                      label={t('cancel') || 'Cancel'}
                      size="small"
                      variant="smoke"
                      onClick={handleCancel}
                    />
                  ]
                : job?.status === 'failed' || job?.status === 'cancelled'
                  ? [
                      <Button
                        key="close"
                        label={t('close') || 'Close'}
                        size="small"
                        variant="smoke"
                        onClick={() => persistJobId(null)}
                      />,
                      <Button
                        key="retry"
                        label={t('retry') || 'Retry'}
                        size="small"
                        variant="primary"
                        onClick={() => {
                          persistJobId(null);
                          handleStart();
                        }}
                        loading={isCreating}
                        disabled={isCreating}
                      />
                    ]
                  : [
                      <Button
                        key="close"
                        label={t('cancel') || 'Cancel'}
                        size="small"
                        variant="smoke"
                        onClick={handleClose}
                      />,
                      <Button
                        key="start"
                        label={t('generate-summary') || 'Generate Summary'}
                        size="small"
                        variant="primary"
                        onClick={handleStart}
                        loading={isCreating}
                        disabled={isCreating}
                      />
                    ]
        }
      >
        <div className="flex flex-col gap-4">
          {/* Quota-exhausted / feature-not-enabled blocking state */}
          {blockReason && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <Lock
                className="h-8 w-8"
                style={{ color: getColour('inactiveText') }}
              />
              {blockReason.kind === 'quota_exhausted' ? (
                <>
                  <p
                    className="text-sm font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('ai-summary-quota-exhausted') ||
                      'Monthly AI summary quota exhausted.'}
                  </p>
                  {blockReason.reset_at && (
                    <p
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('ai-summary-quota-resets') || 'Quota resets on'}{' '}
                      {new Date(blockReason.reset_at).toLocaleDateString()}
                    </p>
                  )}
                </>
              ) : (
                <p
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('ai-summary-not-available') ||
                    'AI summaries are not available on your current plan.'}
                </p>
              )}
            </div>
          )}

          {/* Idle / confirmation state */}
          {!blockReason && !activeJobId && (
            <div className="flex flex-col gap-3">
              <div
                className="text-sm rounded-2xl p-4 border border-hairline"
                style={{
                  backgroundColor: getColour('baseLayer'),
                  color: getColour('normalText')
                }}
              >
                <p className="mb-2">
                  {t('ai-summary-description') ||
                    'Generate a full-document AI summary covering every part of the recording. The result includes a concise summary, key decisions, action items (with owner/due date when stated), and open questions.'}
                </p>
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('ai-summary-language-note') ||
                    'Output language matches the source recording. No translation.'}
                </p>
              </div>

              <Input
                label={t('summary-style') || 'Style and focus (optional)'}
                placeholder={
                  t('ai-summary-style-placeholder') ||
                  'e.g. focus on decisions and next steps, keep it brief'
                }
                multiline
                maxLength={2000}
                value={stylePrompt}
                onChange={e => setStylePrompt(e.target.value)}
              />
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('ai-summary-style-note') ||
                  'Affects tone, focus and format only. Coverage, evidence and the source language are always enforced.'}
              </p>
            </div>
          )}

          {/* Processing state */}
          {!blockReason && isProcessing && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2
                className="h-8 w-8 animate-spin"
                style={{ color: getColour('activeText') }}
              />
              <p
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {job.status === 'queued'
                  ? t('ai-summary-queued') || 'Queued…'
                  : t('ai-summary-processing') || 'Processing…'}
              </p>
              {job.progress > 0 && (
                <div className="w-full max-w-xs">
                  <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: getColour('secondLayer') }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.round(job.progress * 100)}%`,
                        backgroundColor: getColour('activeText')
                      }}
                    />
                  </div>
                  <p
                    className="text-xs text-center mt-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {Math.round(job.progress * 100)}%
                  </p>
                </div>
              )}
              <p
                className="text-xs text-center max-w-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('ai-summary-background-note') ||
                  'You can close this dialog. The summary will be available when you return.'}
              </p>
            </div>
          )}

          {/* Failed state */}
          {!blockReason && job?.status === 'failed' && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                <p className="text-sm font-medium text-red-600">
                  {t('ai-summary-failed') || 'Summary generation failed'}
                </p>
              </div>
              <Button
                label={t('retry') || 'Retry'}
                size="small"
                variant="primary"
                onClick={handleStart}
                loading={isCreating}
              />
            </div>
          )}

          {/* Cancelled state */}
          {!blockReason && job?.status === 'cancelled' && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <XCircle
                  className="h-5 w-5"
                  style={{ color: getColour('inactiveText') }}
                />
                <p
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('ai-summary-cancelled') || 'Summary was cancelled'}
                </p>
              </div>
            </div>
          )}

          {/* Completed state */}
          {!blockReason && job?.status === 'completed' && job.result && (
            <div className="flex flex-col gap-4">
              {/* Revision mismatch warning (#2179) */}
              {revisionMismatch && (
                <div
                  className="flex items-start gap-2 text-xs px-3 py-2 rounded-2xl border"
                  style={{
                    // eslint-disable-next-line no-restricted-syntax
                    backgroundColor: '#fef9c3',
                    // eslint-disable-next-line no-restricted-syntax
                    borderColor: '#fde047',
                    color: getColour('normalText')
                  }}
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-yellow-600" />
                  <span>
                    {t('ai-summary-revision-mismatch') ||
                      'The document has been edited since this summary was generated. Generate a new summary to reflect the latest version.'}
                  </span>
                </div>
              )}
              {/* EU AI Act disclosure (#3937) */}
              <div
                className="flex items-start gap-2 text-xs px-3 py-2 rounded-2xl"
                style={{
                  backgroundColor: getColour('baseLayer'),
                  color: getColour('inactiveText')
                }}
              >
                <Bot className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{aiDisclosure}</span>
              </div>

              {job.result.summary && (
                <SummarySection
                  title={t('summary') || 'Summary'}
                  getColour={getColour}
                >
                  <p className="text-sm leading-relaxed">
                    {job.result.summary}
                  </p>
                </SummarySection>
              )}

              {job.result.key_decisions.length > 0 && (
                <SummarySection
                  title={t('key-decisions') || 'Key Decisions'}
                  getColour={getColour}
                >
                  <ul className="text-sm space-y-1 list-disc list-inside">
                    {job.result.key_decisions.map((d, i) => (
                      <li key={i}>{d.text}</li>
                    ))}
                  </ul>
                </SummarySection>
              )}

              {job.result.action_items.length > 0 && (
                <SummarySection
                  title={t('action-items') || 'Action Items'}
                  getColour={getColour}
                >
                  <ul className="text-sm space-y-2">
                    {job.result.action_items.map((a, i) => (
                      <li key={i} className="flex flex-col gap-0.5">
                        <span>{a.text}</span>
                        <span
                          className="text-xs"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {a.owner !== 'Not specified' &&
                            `${t('owner') || 'Owner'}: ${a.owner} · `}
                          {a.due_date !== 'Not specified' &&
                            `${t('due') || 'Due'}: ${a.due_date}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </SummarySection>
              )}

              {job.result.open_questions.length > 0 && (
                <SummarySection
                  title={t('open-questions') || 'Open Questions'}
                  getColour={getColour}
                >
                  <ul className="text-sm space-y-1 list-disc list-inside">
                    {job.result.open_questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </SummarySection>
              )}

              {job.result.key_decisions.length === 0 &&
                job.result.action_items.length === 0 &&
                job.result.open_questions.length === 0 && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <p
                      className="text-sm"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('ai-summary-no-decisions') ||
                        'No explicit decisions or action items were identified.'}
                    </p>
                  </div>
                )}

              {job.output_language && (
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('output-language') || 'Output language'}:{' '}
                  {job.output_language.toUpperCase()}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

function SummarySection({
  title,
  children,
  getColour
}: {
  title: string;
  children: React.ReactNode;
  getColour: (e: Elements) => string;
}) {
  return (
    <div
      className="rounded-2xl p-3"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <h4
        className="text-xs font-semibold uppercase tracking-wide mb-2"
        style={{ color: getColour('inactiveText') }}
      >
        {title}
      </h4>
      <div style={{ color: getColour('normalText') }}>{children}</div>
    </div>
  );
}

function buildPlainText(
  result: NonNullable<AiSummaryJob['result']>,
  disclosure: string
): string {
  const lines: string[] = [disclosure, '', '# Summary', '', result.summary];

  if (result.key_decisions.length > 0) {
    lines.push('', '## Key Decisions', '');
    result.key_decisions.forEach(d => lines.push(`- ${d.text}`));
  }

  if (result.action_items.length > 0) {
    lines.push('', '## Action Items', '');
    result.action_items.forEach(a => {
      lines.push(`- ${a.text}`);
      if (a.owner !== 'Not specified') lines.push(`  Owner: ${a.owner}`);
      if (a.due_date !== 'Not specified') lines.push(`  Due: ${a.due_date}`);
    });
  }

  if (result.open_questions.length > 0) {
    lines.push('', '## Open Questions', '');
    result.open_questions.forEach(q => lines.push(`- ${q}`));
  }

  return lines.join('\n');
}
